import React from "react";
import { ArrowRight, Check } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const LEVELS = [
    {
        key: "apporteur",
        num: "01",
        badge: "Niveau 1",
        badgeTone: "violet",
        title: "Apporteur",
        description:
            "Tu identifies et introduis des réseaux B2B qualifiés. Pas de suivi actif requis.",
        commission: "Commission sur le volume réel généré",
        profile:
            "Réseau personnel fort, contacts B2B existants prêts à être activés",
        bullets: [
            "Activité ponctuelle, à ton rythme",
            "Suivi pris en charge par CapDistrib",
            "Aucun engagement de volume",
        ],
        cta: "Démarrer comme apporteur",
    },
    {
        key: "business_partner",
        num: "02",
        badge: "Niveau 2 — Recommandé",
        badgeTone: "gold",
        title: "Business Partner",
        description:
            "Tu prospectes, signes et accompagnes tes réseaux. Tu développes le catalogue actif chez tes clients.",
        commission: "Commission récurrente + bonus croissance catalogue",
        profile: "Indépendant commercial, consultant B2B, agent terrain",
        bullets: [
            "Activité commerciale structurée",
            "Bonus sur la diversification catalogue",
            "Outils de pilotage et kit commercial",
        ],
        cta: "Devenir Business Partner",
        featured: true,
    },
    {
        key: "master_partner",
        num: "03",
        badge: "Niveau 3",
        badgeTone: "white",
        title: "Master Partner",
        description:
            "Tu opères une zone géographique ou un vertical entier. Tu animes d'autres partenaires sur ton périmètre.",
        commission: "Commission directe + override sur partenaires animés",
        profile:
            "Fort réseau B2B, expérience gestion de zone ou de vertical",
        bullets: [
            "Périmètre exclusif (zone ou vertical)",
            "Override sur les partenaires de ton périmètre",
            "Co-construction de la stratégie commerciale",
        ],
        cta: "Candidater Master Partner",
    },
];

const FAQ = [
    {
        q: "Faut-il un statut d'entreprise pour rejoindre CapDistrib ?",
        a: "Oui. Un statut professionnel est requis (entreprise individuelle, micro-entreprise, société). Si tu n'en as pas encore, on t'oriente pendant l'entretien de qualification.",
    },
    {
        q: "Combien de temps faut-il consacrer à l'activité ?",
        a: "Cela dépend du niveau choisi. En Apporteur, l'activité est ponctuelle. En Business Partner, compte quelques heures par semaine de prospection et de suivi. En Master Partner, c'est une activité commerciale principale.",
    },
    {
        q: "Quelle est la différence entre Apporteur et Business Partner ?",
        a: "L'Apporteur introduit un réseau et touche une commission sur le volume. Le Business Partner signe le réseau, l'accompagne dans la durée et fait grandir le catalogue actif chez ce client. La commission est plus élevée et récurrente.",
    },
    {
        q: "Puis-je évoluer d'un niveau à l'autre ?",
        a: "Oui. L'évolution est naturelle : beaucoup de Business Partners démarrent en Apporteur. Le passage en Master Partner se fait sur invitation, selon le volume animé et ton périmètre.",
    },
];

const badgeStyles = {
    violet:
        "bg-cap-violet/15 text-cap-violetSoft ring-1 ring-cap-violet/30",
    gold: "bg-cap-gold/15 text-cap-gold ring-1 ring-cap-gold/40",
    white: "bg-white/[0.06] text-white/85 ring-1 ring-white/15",
};

export default function Programme({ onJoin }) {
    return (
        <section
            id="programme"
            className="relative py-24 sm:py-32 bg-cap-ink text-white overflow-hidden"
            data-testid="programme-section"
        >
            <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
            <div className="absolute -top-32 left-1/4 w-[520px] h-[520px] rounded-full bg-cap-violet/30 blur-[140px]" />
            <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-cap-gold/10 blur-[140px]" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <p className="fade-up eyebrow">Le programme partenaire</p>
                    <h2
                        className="fade-up h-display text-3xl sm:text-4xl lg:text-[56px] font-bold mt-4"
                        style={{ animationDelay: "0.05s" }}
                        data-testid="programme-title"
                    >
                        Trois niveaux,
                        <br className="hidden sm:block" /> une{" "}
                        <span className="text-violet-glow">progression naturelle</span>.
                    </h2>
                    <p
                        className="fade-up mt-6 text-lg text-white/65 leading-relaxed max-w-2xl"
                        style={{ animationDelay: "0.1s" }}
                    >
                        Choisis ton niveau d'engagement selon ton profil et ton
                        réseau existant. Tu évolues à ton rythme.
                    </p>
                </div>

                <div className="mt-16 grid lg:grid-cols-3 gap-6">
                    {LEVELS.map((l, i) => (
                        <div
                            key={l.key}
                            className={`fade-up relative glass p-7 sm:p-8 flex flex-col overflow-hidden ${
                                l.featured
                                    ? "border-cap-gold/60 [background:rgba(245,158,11,0.05)] hover:[background:rgba(245,158,11,0.08)] shadow-[0_24px_60px_-20px_rgba(245,158,11,0.35)]"
                                    : ""
                            }`}
                            style={{ animationDelay: `${0.08 * i + 0.1}s` }}
                            data-testid={`programme-card-${l.key}`}
                        >
                            <span className="ghost-num absolute -top-6 -right-2">
                                {l.num}
                            </span>

                            <span
                                className={`inline-flex self-start items-center text-[10.5px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full ${badgeStyles[l.badgeTone]}`}
                                data-testid={`programme-badge-${l.key}`}
                            >
                                {l.badge}
                            </span>

                            <h3 className="h-display mt-7 text-3xl font-bold text-white">
                                {l.title}
                            </h3>

                            <p className="mt-3 text-white/65 leading-relaxed">
                                {l.description}
                            </p>

                            <div
                                className={`mt-6 p-4 rounded-xl border ${
                                    l.featured
                                        ? "bg-cap-gold/10 border-cap-gold/30"
                                        : "bg-white/[0.04] border-white/10"
                                }`}
                            >
                                <p className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-cap-gold">
                                    Rémunération
                                </p>
                                <p className="mt-1 text-white font-semibold">
                                    {l.commission}
                                </p>
                            </div>

                            <ul className="mt-5 space-y-2.5">
                                {l.bullets.map((b) => (
                                    <li
                                        key={b}
                                        className="flex gap-2.5 text-[14.5px] text-white/80"
                                    >
                                        <Check
                                            size={16}
                                            className="text-cap-violetSoft flex-shrink-0 mt-1"
                                        />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-6 pt-5 border-t border-white/10">
                                <p className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-white/40">
                                    Profil cible
                                </p>
                                <p className="mt-1 text-[14px] text-white/80">
                                    {l.profile}
                                </p>
                            </div>

                            <button
                                onClick={() => onJoin(l.key)}
                                className={`mt-7 w-full py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
                                    l.featured ? "btn-gold" : "btn-violet"
                                }`}
                                data-testid={`programme-cta-${l.key}`}
                            >
                                {l.cta}
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    ))}
                </div>

                {/* FAQ */}
                <div className="mt-24 grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-14">
                    <div className="fade-up">
                        <p className="eyebrow">FAQ Programme</p>
                        <h3 className="h-display mt-3 text-2xl sm:text-3xl font-bold text-white">
                            Les questions qu'on nous pose souvent.
                        </h3>
                        <p className="mt-3 text-white/55">
                            Tout ce qu'il faut savoir avant de candidater.
                        </p>
                    </div>
                    <Accordion
                        type="single"
                        collapsible
                        className="fade-up"
                        data-testid="programme-faq"
                    >
                        {FAQ.map((f, idx) => (
                            <AccordionItem
                                key={idx}
                                value={`p-${idx}`}
                                className="border-b border-white/10"
                            >
                                <AccordionTrigger
                                    className="text-left text-[15.5px] font-semibold text-white hover:text-cap-violetSoft py-5"
                                    data-testid={`programme-faq-trigger-${idx}`}
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent
                                    className="text-white/65 leading-relaxed text-[15px] pb-5"
                                    data-testid={`programme-faq-content-${idx}`}
                                >
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}
